<script lang="ts" module>
  import type { HTMLButtonAttributes } from "svelte/elements"

  type TabsTriggerProps = HTMLButtonAttributes & {
    value: unknown
    id?: string | number
    class?: string | undefined | null
    use?: any[]
  }

  type SelectEvent = {
    value: TabsTriggerProps["value"]
    target: EventTarget | null
  }
</script>

<script lang="ts">
  import { generateId, useActions, useCollection } from "#lib/internal/helpers"
  import { cn } from "#lib/utils"
  import type { Snippet } from "svelte"
  import { TAB_STATES } from "."
  import { getTriggerCollection } from "./TabsList.svelte"
  import { getTabsRootContext } from "./TabsRoot.svelte"

  type Props = TabsTriggerProps & {
    onselect?: (event: SelectEvent) => void
    children?: Snippet
  }

  let {
    class: className = undefined,
    value,
    disabled = false,
    id = generateId("tabs-trigger"),
    onselect,
    use = [],
    children,
    ...restProps
  }: Props = $props()

  const rootCtx = getTabsRootContext()
  const triggerCollection = getTriggerCollection()

  const selected = $derived($rootCtx.value === value)

  function activate(event: PointerEvent | FocusEvent) {
    if (event.type === "click" || event.type === "focus") {
      if (event.type === "click" || $rootCtx.activateOn === "focus") {
        $rootCtx.value = value
        onselect?.({ target: event.target, value })
      }
    }
  }
</script>

<button
  {id}
  class={cn(
    [
      "inline-flex",
      "items-center",
      "justify-center",
      "whitespace-nowrap",
      "rounded-xs",
      "px-3",
      "py-1.5",
      "text-sm",
      "font-medium",
      "transition-all",
      "outline-general-focused-state",
      "-outline-offset-2",
      "focus-visible:outline-2",
      "disabled:pointer-events-none",
      "disabled:opacity-50",
      "data-[state=active]:text-icon-general-primary",
    ],
    className
  )}
  data-state={selected ? TAB_STATES.ACTIVE : TAB_STATES.INACTIVE}
  data-orientation={$rootCtx.orientation}
  role="tab"
  tabindex={selected ? 0 : -1}
  {...restProps}
  onclick={activate}
  onfocus={activate}
  use:useActions={use}
  use:useCollection={{ collection: triggerCollection }}
  {disabled}
  data-disabled={disabled || undefined}
>
  {@render children?.()}
</button>
